import { ArrowDownIcon, ArrowUpIcon, CaretSortIcon, Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import type { Header, RowData } from '@tanstack/react-table';
import { useNavigate, type NavigateOptions } from '@tanstack/react-router';
import type { MouseEvent, ReactNode } from 'react';
import { EmptyState, ErrorState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { ListSearch } from '@/lib/url-state';
import type { DataTableColumnMeta, DataTableFeatures } from './columns';
import type { DataTableController } from './use-data-table';

export interface DataTableProps<TData extends RowData, TSearch extends ListSearch> {
  controller: DataTableController<TData, TSearch>;
  /** Accessible name of the table, e.g. "Currencies". */
  label: string;
  /** No data yet (first load). */
  loading?: boolean;
  /** Refetching with the previous page still on screen: dimmed, never a skeleton flash. */
  fetching?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Shown when there are no rows and no filters are active. */
  empty?: ReactNode;
  /**
   * Where a row click goes. The row's identifying cell should also render a real link, which
   * is what keyboard and screen-reader users (and cmd-click) use.
   */
  rowLink?: (row: TData) => NavigateOptions;
  className?: string;
}

const alignClass = (align: DataTableColumnMeta['align']) =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : undefined;

// Clicks on these never trigger row navigation.
const INTERACTIVE = 'a,button,input,select,textarea,label,[role="checkbox"],[role="menuitem"],[data-no-row-click]';

export function DataTable<TData extends RowData, TSearch extends ListSearch>({
  controller,
  label,
  loading = false,
  fetching = false,
  error,
  onRetry,
  empty,
  rowLink,
  className,
}: DataTableProps<TData, TSearch>) {
  const { table, density } = controller;
  const navigate = useNavigate();
  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;
  const hasError = error !== undefined && error !== null;

  const onRowClick = (e: MouseEvent<HTMLTableRowElement>, original: TData) => {
    if (!rowLink || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if ((e.target as HTMLElement).closest(INTERACTIVE)) return;
    if (window.getSelection()?.toString()) return;
    void navigate(rowLink(original));
  };

  let body: ReactNode;
  if (rows.length === 0 && loading) {
    body = Array.from({ length: Math.min(table.state.pagination.pageSize, 8) }, (_, i) => (
      <TableRow key={`skeleton-${i}`} className="hover:bg-transparent">
        {table.getVisibleLeafColumns().map((column) => (
          <TableCell key={column.id}>
            <Skeleton className="h-4 w-full max-w-32" />
          </TableCell>
        ))}
      </TableRow>
    ));
  } else if (rows.length === 0) {
    body = (
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={columnCount} className="whitespace-normal">
          {hasError ? (
            <ErrorState error={error} onRetry={onRetry} compact />
          ) : controller.activeFilters.length > 0 ? (
            <EmptyState
              icon={<MagnifyingGlassIcon />}
              title="No matching results"
              description="Try a different search, or clear the filters."
              action={
                <Button variant="outline" size="sm" onClick={controller.clearFilters}>
                  <Cross2Icon /> Clear filters
                </Button>
              }
            />
          ) : (
            (empty ?? <EmptyState title="Nothing here yet" />)
          )}
        </TableCell>
      </TableRow>
    );
  } else {
    body = rows.map((row) => (
      <TableRow
        key={row.id}
        data-state={row.getIsSelected() ? 'selected' : undefined}
        className={cn(rowLink && 'cursor-pointer')}
        onClick={rowLink ? (e) => onRowClick(e, row.original) : undefined}
      >
        {row.getVisibleCells().map((cell) => {
          const meta = cell.column.columnDef.meta;
          return (
            <TableCell
              key={cell.id}
              className={cn(alignClass(meta?.align), meta?.align === 'right' && 'tabular-nums', meta?.cellClassName)}
            >
              <table.FlexRender cell={cell} />
            </TableCell>
          );
        })}
      </TableRow>
    ));
  }

  return (
    <div className={cn('space-y-2', className)}>
      {hasError && rows.length > 0 && (
        <div className="rounded-md border px-3 py-2">
          <ErrorState error={error} onRetry={onRetry} title="Couldn't refresh this list" compact />
        </div>
      )}
      {/* Clips the header band to the rounded corners. */}
      <div className="overflow-hidden rounded-md border">
        <Table
          aria-label={label}
          aria-busy={loading || fetching || undefined}
          data-density={density}
          className={cn(
            'transition-opacity',
            density === 'compact' ? '[&_td]:py-1 [&_th]:h-8' : '[&_td]:py-2.5',
            fetching && rows.length > 0 && 'opacity-60',
          )}
        >
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id} className="hover:bg-transparent">
                {group.headers.map((header) => (
                  <DataTableHead key={header.id} header={header} controller={controller} />
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>{body}</TableBody>
        </Table>
      </div>
    </div>
  );
}

function DataTableHead<TData extends RowData, TSearch extends ListSearch>({
  header,
  controller,
}: {
  header: Header<DataTableFeatures, TData, unknown>;
  controller: DataTableController<TData, TSearch>;
}) {
  const { table } = controller;
  const meta = header.column.columnDef.meta;
  const sortable = Boolean(meta?.sortField) && !header.isPlaceholder;
  const sorted = header.column.getIsSorted();
  const content = header.isPlaceholder ? null : <table.FlexRender header={header} />;

  // asc → desc → back to the default sort. Only one sort applies server-side. The column the
  // default sorts on starts out sorted, and "back to the default" would change nothing there, so
  // its first click reverses it.
  const toggle = () => {
    if (sorted && !controller.search.sort) table.setSorting([{ id: header.column.id, desc: sorted === 'asc' }]);
    else if (sorted === 'asc') table.setSorting([{ id: header.column.id, desc: true }]);
    else if (sorted === 'desc') table.setSorting([]);
    else table.setSorting([{ id: header.column.id, desc: false }]);
  };

  return (
    <TableHead
      aria-sort={sortable ? (sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none') : undefined}
      className={cn(alignClass(meta?.align), meta?.headerClassName)}
    >
      {sortable ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={toggle}
          className={cn('h-7 font-medium', meta?.align === 'right' ? '-mr-2.5 ml-auto flex' : '-ml-2.5')}
        >
          {content}
          {sorted === 'asc' ? (
            <ArrowUpIcon aria-hidden />
          ) : sorted === 'desc' ? (
            <ArrowDownIcon aria-hidden />
          ) : (
            <CaretSortIcon aria-hidden className="text-muted-foreground" />
          )}
        </Button>
      ) : (
        content
      )}
    </TableHead>
  );
}
